import React, { useState, useEffect } from "react";
import { useTeams } from "@/pages/applications/hooks/useTeams";
import TeamStatCard, { TeamData } from "./TeamStatCard";
import { useLanguage } from "@/contexts/LanguageContext";
import { Skeleton } from "@/components/ui/skeleton";
import { supabase } from "@/integrations/supabase/client";
import { TeamStat } from "@/pages/scheduling/types";
import { 
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from "@/components/ui/collapsible";
import { ChevronDown, ChevronUp } from "lucide-react";
import { cn } from "@/lib/utils";

interface TeamStatCardsProps {
  selectedTeamId: string | null;
  onTeamChange: (teamId: string) => void;
}

const TeamStatCards: React.FC<TeamStatCardsProps> = ({ 
  selectedTeamId, 
  onTeamChange 
}) => {
  const { t } = useLanguage();
  const { data: teams, isLoading: isTeamsLoading } = useTeams();
  const [teamsWithCount, setTeamsWithCount] = useState<TeamStat[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(true);
  
  useEffect(() => {
    if (selectedTeamId) {
      const timer = setTimeout(() => {
        setIsOpen(false);
      }, 500);
      return () => clearTimeout(timer);
    } else {
      setIsOpen(true);
    }
  }, [selectedTeamId]);
  
  useEffect(() => {
    const fetchTeamMemberCounts = async () => {
      if (!teams || teams.length === 0) {
        setIsLoading(false);
        return;
      }
      
      try {
        const teamsWithGuardCounts = await Promise.all(teams.map(async (team) => {
          const { data, error } = await supabase
            .from('team_members')
            .select('profile_id')
            .eq('team_id', team.id);
            
          if (error) {
            console.error("Error fetching team members:", error);
            throw error;
          }
          
          const teamStat: TeamStat = {
            teamId: team.id,
            teamName: team.name,
            guardsCount: data?.length || 0,
            value: data?.length || 0,
            label: team.name,
            description: t("totalGuards") || "Total Guards"
          };
          
          return teamStat;
        }));
        
        // Sort teams alphabetically by name
        const sortedTeams = teamsWithGuardCounts.sort((a, b) => 
          a.teamName.localeCompare(b.teamName, 'el', { sensitivity: 'base' })
        );
        
        setTeamsWithCount(sortedTeams);
      } catch (error) {
        console.error("Error fetching team guard counts:", error);
      } finally {
        setIsLoading(false);
      }
    };
    
    if (teams) {
      fetchTeamMemberCounts();
    }
  }, [teams, t]);

  if (isTeamsLoading || isLoading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4 mb-6">
        {Array.from({ length: 4 }).map((_, index) => (
          <Skeleton key={index} className="h-24 w-full" />
        ))}
      </div>
    );
  }

  if (!teams || teams.length === 0) {
    return (
      <div className="text-center p-4 bg-muted/20 rounded-lg">
        <p className="text-muted-foreground">{t("noTeamsAvailable") || "No teams available"}</p>
      </div>
    );
  }

  return (
    <Collapsible
      open={isOpen}
      onOpenChange={setIsOpen}
      className="mb-6 transition-all duration-700 ease-in-out"
    >
      <div className="flex items-center justify-between mb-2">
        {selectedTeamId && (
          <CollapsibleTrigger className="hover:bg-muted/20 p-2 rounded-full transition-all duration-300 hover:scale-105">
            {isOpen 
              ? <ChevronUp className="h-4 w-4 text-muted-foreground" />
              : <ChevronDown className="h-4 w-4 text-muted-foreground" />
            }
          </CollapsibleTrigger>
        )}
      </div>
      
      <CollapsibleContent className={cn(
        "transition-all duration-700 overflow-hidden",
        "data-[state=open]:animate-[accordion-down_0.7s_ease-out]",
        "data-[state=closed]:animate-[accordion-up_0.7s_ease-out]"
      )}>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4 animate-fade-in">
          {teamsWithCount.map((team) => (
            <TeamStatCard
              key={team.teamId}
              team={{
                id: team.teamId,
                name: team.teamName,
                guardsCount: team.guardsCount
              }}
              isSelected={selectedTeamId === team.teamId}
              onClick={onTeamChange}
            />
          ))}
        </div>
      </CollapsibleContent>
      
      {selectedTeamId && !isOpen && (
        <div 
          className="cursor-pointer flex items-center justify-between p-3 bg-primary/5 rounded-lg mt-1 transition-all duration-500 hover:bg-primary/10 transform hover:scale-[1.01] shadow-sm hover:shadow animate-fade-in"
          onClick={() => setIsOpen(true)}
        >
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">
              {teamsWithCount.find(t => t.teamId === selectedTeamId)?.teamName || ""}
            </span>
            <span className="text-xs text-muted-foreground">
              ({t("clickToExpandTeams") || "Click to expand"})
            </span>
          </div>
          <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform duration-300 group-hover:translate-y-1" />
        </div>
      )}
    </Collapsible>
  );
};

export default TeamStatCards;
